<template>
  <div
    class="timeline-scrubber"
    :class="{ 'initial-load': !initialLoad, scrubbing: animatingSeek }"
    ref="root"
    role="slider"
    :aria-valuemin="Math.floor((centerTs - halfWindowMs) / 1000)"
    :aria-valuemax="Math.ceil((centerTs + halfWindowMs) / 1000)"
    :aria-valuenow="Math.floor(centerTs / 1000)"
    aria-label="Timeline scrubber"
    tabindex="0"
  >
    <div class="buttons">
      <button
        @click="maybeSetWindowSize(windowSizeMinutes / 2)"
      >
        &plus;
      </button>
      <button
        @click="maybeSetWindowSize(windowSizeMinutes * 2)"
      >
        &minus;
      </button>
    </div>
    <svg
      ref="svgEl"
      :width="width"
      :height="height"
      class="timeline-svg"
      @pointerdown="onPointerDown"
      @pointerenter="onPointerEnter"
      @pointermove="onLocalPointerMove"
      @pointerleave="onPointerLeave"
    >
      <!-- Dashed pattern for future overlay -->
      <defs>
        <pattern
          id="futureHatch"
          class="future-overlay"
          patternUnits="userSpaceOnUse"
          width="4"
          height="4"
          patternTransform="rotate(45)"
        >
          <line
            x1="0"
            y1="0"
            x2="0"
            y2="4"
            stroke="currentColor"
            stroke-width="2"
            vector-effect="non-scaling-stroke"
          />
        </pattern>
      </defs>

      <!-- Ticks (major, minor, sub) -->
      <g class="ticks">
        <template v-for="t in ticks">
          <g :key="t.x + ':' + t.level">
          <line
            :x1="t.x"
            :x2="t.x"
            :y1="0"
            :y2="t.height"
            :stroke="'currentColor'"
            :stroke-width="t.level === 2 ? 2 : 1"
            :class="[
              'tick',
              t.level === 2 ? 'major' : t.level === 1 ? 'minor' : 'sub',
              t.ts > nowTs ? 'future' : '',
            ]"
          />
          <text
            v-if="t.level === 2"
            :x="t.x + 4"
            :y="height - 4"
            :class="['tick-label', t.ts > nowTs ? 'future' : '']"
          >
            {{ formatTime(t.ts) }}
          </text>
          </g>
        </template>
      </g>

      <!-- Day boundaries -->
      <g class="day-lines">
        <template v-for="d in dayTicks">
          <g :key="'day-' + d.x">
          <line :x1="d.x" :x2="d.x" y1="0" :y2="height" class="day-line" />
          <template v-if="hideDateLabel(d.x)">
            <rect :x="d.x + 4" y="4" width="60" height="12" class="day-label" />
            <text :x="d.x + 4" y="14" class="day-label">
              {{ d.label }}
            </text>
          </template>
          </g>
        </template>
      </g>

      <!-- Center line (playhead) -->
      <line :x1="midX" :x2="midX" y1="0" :y2="height" class="center-line" />

      <!-- Event bars -->
      <g class="timeline-events" v-if="visibleEvents.length">
        <line
          v-for="(ev, i) in visibleEvents"
          :key="'ev-' + i"
          :x1="ev.x1"
          :x2="ev.x2"
          :y1="eventY"
          :y2="eventY"
          class="event-bar"
        />
      </g>
      <!-- Hover preview (thin accent line + time label) -->
      <g v-show="hovering && !dragging" class="hover-preview">
        <line
          :x1="hoverX"
          :x2="hoverX"
          y1="0"
          :y2="height"
          class="preview-line"
        />
        <template v-if="hoveredEventIconHref">
          <svg
            v-for="(icon, iconIdx) in hoveredEventIconHref"
            :key="iconIdx"
            :x="hoverX - 16 * (hoveredEventIconHref.length - iconIdx)"
            :y="4"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            class="hover-event-icon"
            fill="currentColor"
          >
            <use :href="icon + '#symbol'"></use>
          </svg>
        </template>
      </g>
      <!-- Future overlay (after 'now') -->
      <g class="future-overlay" v-if="futureWidth > 0">
        <rect :x="futureX" y="0" :width="futureWidth" :height="height" />
        <rect
          :x="futureX"
          y="0"
          :width="futureWidth"
          :height="height"
          fill="url(#futureHatch)"
        />
      </g>
    </svg>
  </div>
</template>

<script>
const S = 1000;
const M = S * 60;
const H = M * 60;

const MIN_MINOR_SPACING_PX = 30;

const timeDivisionsSub = [
  // seconds
  1 * S,
  2 * S,
  3 * S,
  5 * S,
  10 * S,
  15 * S,
  20 * S,
  30 * S,
  // minutes
  1 * M,
  2 * M,
  3 * M,
  5 * M,
  10 * M,
  15 * M,
  20 * M,
  30 * M,
  // hours
  1 * H,
  2 * H,
  3 * H,
  4 * H,
  6 * H,
  8 * H,
  12 * H,
  24 * H,
];

const timeDivisionsMinorMajor = [
  [1 / 4, 1],
  [1, 5],
  [5, 15],
  [15, 60],
  [30, 60],
  [60, 60 * 4],
  [60 * 2, 60 * 6],
  [60 * 3, 60 * 12],
];

const Clamp = (x, min, max) => Math.max(min, Math.min(x, max));

function findBestTimeDivision(N) {
  const multiplier = 60 * 1000;

  const found = timeDivisionsMinorMajor.find(([a]) => a >= N);
  if (found) { const [a, b] = found; return [a * multiplier, b * multiplier]; }

  // Not found: scale the last pair by the minimal power of two
  const [lastA, lastB] = timeDivisionsMinorMajor[timeDivisionsMinorMajor.length - 1];
  const k = Math.max(0, Math.ceil(Math.log2(N / lastA)));
  const factor = 2 ** k;
  return [lastA * factor * multiplier, lastB * factor * multiplier];
}

// Choose a sub-unit that divides the minor unit and yields clean pixel spacing.
function chooseSubUnit(minorMs, stepMinorPx) {
  const meaningful = timeDivisionsSub
    .filter((u) => u < minorMs && minorMs % u === 0)
    .sort((a, b) => a - b);

  const ok = meaningful.find((candidateSubStep) => {
    const numSubSteps = minorMs / candidateSubStep;
    return numSubSteps >= 2 && numSubSteps < stepMinorPx / 2;
  });
  if (ok) { const numSubSteps = minorMs / ok; return { subUnitMs: ok, N: numSubSteps }; }

  // Fallback: pick a divisor of the pixel step (keeps equal pixel spacing)
  const divisorsPref = [2, 3, 4, 5, 6, 8, 10, 12];
  const N = divisorsPref.find((n) => stepMinorPx % n === 0 && n * 2 <= stepMinorPx);
  if (N) { return { subUnitMs: Math.floor(minorMs / N), N }; }

  // Last resort: disable sub-markers
  return { subUnitMs: 0, N: 0 };
}

export default {
  name: 'TimelineScrubber',
  props: {
    /** Current playhead timestamp in ms (centered red line). */
    modelValue: { type: Number, required: true },
    /** Visible window width in minutes (total, centered around modelValue). */
    windowMinutes: { type: Number, default: 300 },
    /** Major tick every N minutes. */
    majorEveryMin: { type: Number, default: 15 },
    /** Minor tick every N minutes. */
    minorEveryMin: { type: Number, default: 5 },
    /** Scrub debounce in ms. */
    debounceMs: { type: Number, default: 1000 },
    /** Fixed height in px. */
    height: { type: Number, default: 36 },
    /** Show boundaries between days */
    showDayBoundaries: { type: Boolean, default: true },
    /** Events to render. */
    events: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'seek'],
  data() {
    return {
      hoveredEvent: null,
      nowTs: Date.now(),
      nowTimer: null,
      width: 600,
      dragging: false,
      dragStartX: 0,
      dragStartTs: 0,
      centerTs: this.modelValue,
      ro: null,
      raf: 0,
      initialLoad: false,

      smoothSeekRaf: 0,
      debounceTimer: null,
      freezeMoves: false,
      windowSizeMinutes: this.windowMinutes,
      hovering: false,
      hoverX: 0,
      lastClientX: 0,
      dragMoved: false,
      dragPointerId: null,
      dragTargetEl: null,
      animatingSeek: false,
      animSeekCancel: null,
    };
  },
  computed: {
    eventY() {
      return Math.round(this.height * 0.475);
    },
    visibleEvents() {
      const { baseUnitMs, stepBasePx } = this.scaleConfig;
      const baseMsPerPx = baseUnitMs / stepBasePx;
      const { tsCenter, xCenter } = this.anchorConfig;
      const leftTs = tsCenter - xCenter * baseMsPerPx;
      const rightTs = tsCenter + (this.width - xCenter) * baseMsPerPx;
      const startW = leftTs;
      const endW = Math.min(this.nowTs, rightTs);
      const items = Array.isArray(this.events) ? this.events : [];
      const out = items
        .map((it) => ({ s: Number(it.s), e: Number(it.e), it }))
        .filter(({ s }) => Number.isFinite(s))
        .filter(({ s, e }) => !(e < startW || s > endW))
        .map(({ s, e, it }) => {
          const x1 = this.tsToX(s);
          const x2 = this.tsToX(e);
          return {
            s,
            e,
            x1: x1 - 1,
            x2: x2 + 1,
            iconHref: it?.iconHref || null,
            raw: it,
          };
        });
      return out;
    },
    hoveredEventIconHref() {
      return this.hoveredEvent?.iconHref || null;
    },

    windowStart() {
      return this.centerTs - this.halfWindowMs;
    },
    windowEnd() {
      return this.centerTs + this.halfWindowMs;
    },
    halfWindowMs() {
      return Math.max(1, (this.windowSizeMinutes * 60 * 1000) / 2);
    },
    pxPerMs() {
      return this.width / (this.halfWindowMs * 2);
    },
    scaleConfig() {
      const minMinorSpacingMin = MIN_MINOR_SPACING_PX / this.pxPerMs / (1000 * 60);
      const [minorMs, majorMs] = findBestTimeDivision(minMinorSpacingMin);

      const stepMinorPx = Math.max(1, Math.round(this.pxPerMs * minorMs));
      const { subUnitMs } = chooseSubUnit(minorMs, stepMinorPx);

      const baseUnitMs = subUnitMs > 0 ? subUnitMs : minorMs;
      const stepBasePx = Math.max(1, Math.round(this.pxPerMs * baseUnitMs));

      const minorEvery = Math.max(1, Math.round(minorMs / baseUnitMs));
      const majorEvery = Math.max(1, Math.round(majorMs / baseUnitMs));

      return {
        minorMs,
        majorMs,
        stepMinorPx,
        subUnitMs,
        baseUnitMs,
        stepBasePx,
        minorEvery,
        majorEvery,
      };
    },
    midX() {
      return Math.floor(this.width / 2);
    },
    anchorConfig() {
      const { baseUnitMs, stepBasePx } = this.scaleConfig;
      const day0Ts = this.getLocalMidnightTs(this.centerTs);
      const kCenter = Math.round((this.centerTs - day0Ts) / baseUnitMs);
      const tsCenter = day0Ts + kCenter * baseUnitMs;
      const xCenter = this.midX + Math.round((tsCenter - this.centerTs) * this.pxPerMs);
      return {
        day0Ts, kCenter, tsCenter, xCenter, stepBasePx,
      };
    },
    ticks() {
      return this.buildTicks();
    },
    dayTicks() {
      if (!this.showDayBoundaries) return [];
      const seen = new Set();
      const out = [];
      this.ticks.forEach((t) => {
        if (this.isLocalMidnight(t.ts) && !seen.has(t.ts)) {
          out.push({ x: t.x, ts: t.ts, label: this.formatDate(t.ts) });
          seen.add(t.ts);
        }
      });
      return out;
    },
    futureX() {
      const start = this.windowStart;
      const end = this.windowEnd;
      const n = this.nowTs;
      if (n <= start) return 0;
      if (n >= end) return this.width;
      return Math.max(
        0,
        Math.min(this.width, Math.round((n - start) * this.pxPerMs)),
      );
    },
    futureWidth() {
      return Math.max(0, this.width - this.futureX);
    },
  },
  watch: {
    modelValue(v, oldV) {
      if (!this.freezeMoves && !this.dragging) {
        this.animateSmoothSeek(v);
      }
      if (oldV !== 0 && v !== 0 && v - oldV < 1500) {
        this.initialLoad = true;
      }
    },
  },
  mounted() {
    this.nowTimer = setInterval(() => {
      this.nowTs = Date.now();
    }, 1000);
    this.centerTs = this.modelValue;
    // Resize observer to track width
    this.ro = new ResizeObserver((entries) => {
      entries.forEach((e) => {
        const cr = e.contentRect;
        const w = Math.max(200, Math.floor(cr.width));
        if (w !== this.width) {
          this.width = w;
          this.queueFrame();
        }
      });
    });
    this.ro.observe(this.$refs.root);
    // Pointer capture end safety
    this.bindGlobalEndListeners();
  },
  beforeUnmount() {
    try {
      if (this.nowTimer) clearInterval(this.nowTimer);

      if (this.smoothSeekRaf) cancelAnimationFrame(this.smoothSeekRaf);
    } catch {
      /* Ignore */
    }
    try {
      if (this.ro) this.ro.disconnect();
    } catch {
      /* Ignore */
    }
    this.unbindGlobalEndListeners();
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    maybeSetWindowSize(targetSize) {
      if (targetSize >= 15 && targetSize <= 60 * 6) {
        this.windowSizeMinutes = targetSize;
        return true;
      }
      return false;
    },
    // ---- Event helpers (respect subunit scaling) ----
    tsToX(ts) {
      const { baseUnitMs, stepBasePx } = this.scaleConfig;
      const baseMsPerPx = baseUnitMs / stepBasePx;
      const alignedCenter = this.centerTs - (this.centerTs % baseMsPerPx);
      const n = Math.min(this.nowTs, Number(ts));
      const diffMs = alignedCenter - n;
      const scaledN = diffMs / baseMsPerPx;
      const x = this.width / 2 - scaledN;
      return Math.round(Clamp(x, 0, this.width));
    },
    findEventAtTs(ts) {
      const t = Clamp(ts, this.windowStart, this.nowTs);
      const best = this.visibleEvents.reduce((acc, e) => {
        if (t >= e.s && t <= e.e) {
          if (!acc || e.e - e.s < acc.e - acc.s) return e;
        }
        return acc;
      }, null);
      return best;
    },

    // ---- Geometry / conversions ----
    clientToLocalX(clientX) {
      const r = this.$refs.root?.getBoundingClientRect?.();
      if (!r) return 0;
      return Clamp(Math.round(clientX - r.left), 0, this.width);
    },
    xToTs(x) {
      const { baseUnitMs, stepBasePx } = this.scaleConfig;
      const baseMsPerPx = baseUnitMs / stepBasePx;
      const diff = this.width / 2 - x;
      const alignedCenter = this.centerTs - (this.centerTs % baseUnitMs);
      const ts = alignedCenter - diff * baseMsPerPx;
      return Math.min(this.nowTs, Math.max(this.windowStart, ts));
    },
    // ---- Pointer / interaction ----
    onPointerEnter(e) {
      if (!this.initialLoad) return;
      if (this.dragging) return;
      this.hovering = true;
      this.lastClientX = e.clientX;
      this.hoverX = this.clientToLocalX(e.clientX);
    },
    onLocalPointerMove(e) {
      if (!this.initialLoad) return;
      this.lastClientX = e.clientX;
      const x = this.clientToLocalX(e.clientX);
      if (!this.dragging) {
        this.hovering = true;
        this.hoverX = x;
        const ts = this.xToTs(x);
        this.hoveredEvent = this.findEventAtTs(ts);
      } else if (Math.abs(this.dragStartX - e.clientX) > 4) {
        this.dragMoved = true;
      }
    },
    onPointerLeave() {
      if (!this.initialLoad) return;
      this.hovering = false;
      this.hoveredEvent = null;
    },
    onPointerDown(e) {
      if (!this.initialLoad) return;
      this.cancelSmoothSeek();
      this.dragging = true;
      this.freezeMoves = true;

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }

      this.dragStartX = e.clientX;
      this.dragStartTs = this.centerTs;
      this.lastClientX = e.clientX;
      this.dragMoved = false;

      this.dragPointerId = e.pointerId;
      this.dragTargetEl = e.currentTarget || this.$refs.svgEl;

      this.dragTargetEl.setPointerCapture(this.dragPointerId);
      this.dragTargetEl.addEventListener(
        'pointermove',
        this.onPointerMove,
        { passive: true },
      );
      this.dragTargetEl.addEventListener(
        'lostpointercapture',
        this.onPointerUp,
        { passive: true },
      );
    },
    onPointerMove(e) {
      if (!this.initialLoad) return;
      if (!this.dragging) return;
      this.freezeMoves = true;
      const dx = this.dragStartX - e.clientX;
      if (Math.abs(dx) > 4) this.dragMoved = true;
      const dMs = Math.round(dx / this.pxPerMs);
      let newTs = this.dragStartTs + dMs;
      newTs = Math.min(newTs, this.nowTs);
      this.moveCenter(newTs);
    },
    onPointerUp() {
      if (!this.initialLoad) return;
      if (!this.dragging) return;

      if (this.dragTargetEl) {
        try {
          this.dragTargetEl.releasePointerCapture(this.dragPointerId);
        } catch {
          /* Ignore */
        }
        this.dragTargetEl.removeEventListener(
          'pointermove',
          this.onPointerMove,
        );
        this.dragTargetEl.removeEventListener(
          'lostpointercapture',
          this.onPointerUp,
        );
      }
      this.dragTargetEl = null;
      this.dragPointerId = null;

      if (!this.dragMoved) {
        // Click-to-seek: emit immediately
        const localX = this.clientToLocalX(this.lastClientX);
        const tsClick = this.xToTs(localX);
        const ev = this.hoveredEvent || this.findEventAtTs(tsClick);
        // Snap to event start only if we are not already inside that event
        let targetTs = tsClick;
        if (ev) {
          const inside = Number(ev.s) - 1000 <= this.centerTs
              && this.centerTs <= Number(ev.e) + 1000;
          targetTs = inside ? tsClick : Number(ev.s);
        }
        this.emitNow(targetTs);
        // this.animateSmoothSeek(targetTs);
      } else {
        this.dragging = false;
        this.debouncedEmit(this.centerTs);
        this.dragMoved = false;
        return;
      }

      this.dragging = false;
      this.dragMoved = false;
    },

    // ---- Emission / scheduling ----
    // ---- Global end/abort handlers ----
    bindGlobalEndListeners() {
      // Bind once; store a flag to avoid duplicates
      if (this.endListenersBound) return;
      this.endListenersBound = true;
      const opts = { passive: true };
      window.addEventListener('pointerup', this.onPointerUp, opts);
      window.addEventListener('pointercancel', this.onPointerUp, opts);
      window.addEventListener('mouseup', this.onPointerUp, opts);
      window.addEventListener('touchend', this.onPointerUp, opts);
      window.addEventListener('touchcancel', this.onPointerUp, opts);
      window.addEventListener('contextmenu', this.onPointerUp, opts);
      window.addEventListener('blur', this.onWindowBlur, opts);
      document.addEventListener(
        'visibilitychange',
        this.onVisibilityChange,
        opts,
      );
      document.addEventListener('keydown', this.onGlobalKeyDown, false);
    },
    unbindGlobalEndListeners() {
      if (!this.endListenersBound) return;
      this.endListenersBound = false;
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      window.removeEventListener('mouseup', this.onPointerUp);
      window.removeEventListener('touchend', this.onPointerUp);
      window.removeEventListener('touchcancel', this.onPointerUp);
      window.removeEventListener('contextmenu', this.onPointerUp);
      window.removeEventListener('blur', this.onWindowBlur);
      document.removeEventListener(
        'visibilitychange',
        this.onVisibilityChange,
      );
      document.removeEventListener('keydown', this.onGlobalKeyDown, false);
    },
    onWindowBlur() {
      if (this.dragging) this.onPointerUp();
    },
    onVisibilityChange() {
      if (document.hidden && this.dragging) this.onPointerUp();
    },
    onGlobalKeyDown(e) {
      if (e.key === 'Escape' && this.dragging) {
        // Treat as cancel: don't emit a click seek; keep center where it is
        this.dragMoved = false;
        this.onPointerUp();
      }
    },

    emitNow(oTs) {
      const ts = Math.round(oTs);
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      const seek = new Date(Math.min(ts, new Date().getTime()));
      if (new Date().getTime() - seek.getTime() < 1000) {
        this.$emit('update:modelValue', ts);
        this.$emit('seek', ts);
        this.animateSmoothSeek(ts);
        setTimeout(() => {
          this.freezeMoves = false;
        }, 3000);
        return;
      }

      if (!this.maybeSetWindowSize(this.windowSizeMinutes / 2)) {
        while (this.maybeSetWindowSize(this.windowSizeMinutes * 2)) { /* Pass */ }
      }
    },
    debouncedEmit(ts) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        if (this.dragging) return;
        this.$emit('update:modelValue', ts);
        this.$emit('seek', ts);
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
        setTimeout(() => {
          this.freezeMoves = false;
        }, 3000);
      }, this.debounceMs);
    },
    queueFrame() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
      });
    },

    // ---- Smooth click-to-seek animation ----
    cancelSmoothSeek() {
      if (this.smoothSeekRaf) {
        try {
          cancelAnimationFrame(this.smoothSeekRaf);
        } catch {
          /* Ignore */
        }
        this.smoothSeekRaf = 0;
      }
    },
    animateSmoothSeek(targetTs, opts = {}) {
      const { pixelsPerSecond = 1600, easing = (t) => t * (2 - t) } = opts;

      if (typeof this.animSeekCancel === 'function') this.animSeekCancel();

      const currentAtPlayhead = this.centerTs;
      const deltaMsTotal = Number(targetTs) - Number(currentAtPlayhead);
      if (!Number.isFinite(deltaMsTotal) || deltaMsTotal === 0) return;
      if (Math.abs(deltaMsTotal) <= 5 * (1 / this.pxPerMs)) {
        this.moveCenter(targetTs);
        return;
      }

      // Lock scale during the animation so tick layout doesn't jitter.
      const width = this.width || 1;
      const halfWindow = this.halfWindowMs || 1;
      const pxPerMsLocked = width / (2 * halfWindow);

      const deltaPxTotal = deltaMsTotal * pxPerMsLocked;
      let durationMs = (Math.abs(deltaPxTotal) / pixelsPerSecond) * 1000;
      if (!Number.isFinite(durationMs)) durationMs = 600;
      durationMs = Clamp(durationMs, 200, 600);

      this.animatingSeek = true;

      let stopped = false;
      const startCenter = this.centerTs;
      const endCenter = startCenter + deltaMsTotal;
      const t0 = performance.now();

      const stop = () => {
        if (stopped) return;
        stopped = true;
        this.animatingSeek = false;
        this.animSeekCancel = null;
      };
      this.animSeekCancel = stop;

      const step = (now) => {
        if (stopped) return;
        const t = Math.min(1, (now - t0) / durationMs);
        const k = easing(t);

        const curPx = deltaPxTotal * k;
        const curMs = curPx / pxPerMsLocked;
        const nextCenter = startCenter + curMs;

        this.centerTs = nextCenter;

        if (t < 1 && this.dragging === false) {
          requestAnimationFrame(step);
        } else {
          this.centerTs = endCenter;
          stop();
        }
      };

      requestAnimationFrame(step);
    },

    // ---- Formatting ----
    formatDate(ts) {
      const d = new Date(ts);
      const wd = d.toLocaleDateString(undefined, { weekday: 'short' });
      const mon = d.toLocaleDateString(undefined, { month: 'short' });
      const day = String(d.getDate()).padStart(2, '0');
      return `${wd} ${day} ${mon}`;
    },
    formatTime(ts) {
      const d = new Date(ts);
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      const s = String(d.getSeconds()).padStart(2, '0');
      if (this.scaleConfig.baseUnitMs < M) return `${h}:${m}:${s}`;

      return `${h}:${m}`;
    },

    // ---- Layout helpers ----
    moveCenter(v) {
      if (Math.abs(this.centerTs - v) > 1 / this.pxPerMs) this.centerTs = v;
    },
    getMinorSpacingHieght(ts, minor) {
      const idx = timeDivisionsMinorMajor.findIndex(([a]) => a === minor / 60000)
          || timeDivisionsMinorMajor.length + 1;
      const scale = Math.sqrt(
        (idx < 0 ? timeDivisionsMinorMajor.length : idx)
            / timeDivisionsMinorMajor.length,
      );
      return 0.8 * scale * this.height;
    },
    getSubSpacingHeight(ts, minor) {
      if (ts % 60000 !== 0) {
        if (ts % 15000 === 0) return 4;
        if (ts % 5000 === 0) return 2;
        return 1;
      }
      if ((ts / 60000) % 5 === 0) {
        return Math.max(
          5,
          Math.min(this.getMinorSpacingHieght(ts, minor) / 2, 8),
        );
      }
      return 6;
    },
    isLocalMidnight(ts) {
      const d = new Date(ts);
      return (
        d.getHours() === 0
          && d.getMinutes() === 0
          && d.getSeconds() === 0
          && d.getMilliseconds() === 0
      );
    },
    getLocalMidnightTs(ts) {
      const d = new Date(ts);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    },
    hideDateLabel(x) {
      return (
        (this.width / 2 - x > 64 || this.width / 2 - x < -32)
          && (this.dragMoved || this.hoverX - x > 64 || this.hoverX - x < -32)
      );
    },

    // ---- Tick builder (uses split configs) ----
    buildTicks() {
      const {
        minorMs, baseUnitMs, stepBasePx, minorEvery, majorEvery,
      } = this.scaleConfig;
      const { kCenter, tsCenter, xCenter } = this.anchorConfig;

      const out = [];
      const pushTick = (k, ts, x) => {
        if (x < -stepBasePx || x > this.width + stepBasePx) return;

        const isMajor = k % majorEvery === 0;
        const isMinor = !isMajor && k % minorEvery === 0;
        let level;
        if (isMajor) level = 2;
        else level = isMinor ? 1 : 0;
        const { height } = this;

        let trueHeight;
        if (isMajor) trueHeight = height;
        else {
          trueHeight = isMinor
            ? this.getMinorSpacingHieght(ts, minorMs)
            : this.getSubSpacingHeight(ts, minorMs);
        }

        out.push({
          x,
          ts,
          level,
          major: isMajor,
          sub: !isMajor && !isMinor,
          height: trueHeight,
        });
      };

      // Center-aligned base tick
      pushTick(kCenter, tsCenter, xCenter);

      // March outward symmetrically
      const maxSteps = Math.ceil(Math.max(xCenter, this.width - xCenter) / stepBasePx) + 2;

      Array.from({ length: maxSteps }, (_, idx) => idx + 1).forEach((i) => {
        // Right
        const kR = kCenter + i;
        const tsR = tsCenter + i * baseUnitMs;
        const xR = xCenter + i * stepBasePx;
        pushTick(kR, tsR, xR);

        // Left
        const kL = kCenter - i;
        const tsL = tsCenter - i * baseUnitMs;
        const xL = xCenter - i * stepBasePx;
        pushTick(kL, tsL, xL);
      });

      return out;
    },
    labelTsFromX(x) {
      return Math.round(this.windowStart + x / this.pxPerMs);
    },
  },
};
</script>

<style scoped>
  .timeline-scrubber {
    position: relative;
    cursor: ew-resize;
    box-shadow: inset 0 1px 0 rgba(0, 0, 0, 0.06);
    background: var(--surface);
    width: 100%;
    touch-action: pan-x;
    user-select: none;
  }
  .timeline-scrubber.initial-load {
    pointer-events: none;
  }
  .timeline-scrubber.initial-load,
  .timeline-scrubber.scrubbing {
    cursor: wait;
    pointer-events: none;
  }
  .timeline-svg {
    display: block;
    color: var(--text);
    fill: var(--text);
    shape-rendering: crispEdges;
    touch-action: none;
  }
  .center-line {
    stroke: var(--accent);
    stroke-width: 2.5;
  }
  .tick-label {
    font: 12px/1 system-ui;
    dominant-baseline: ideographic;
    opacity: 0.9;
  }

  .future-overlay rect:first-child {
    fill: black;
    opacity: 0.2;
    pointer-events: none;
  }
  .future-overlay line {
    opacity: 0.2;
    color: var(--text);
  }

  .tick {
    fill: var(--text);
  }
  .tick.future,
  .tick-label.future {
    opacity: 0.35;
  }

  /* Visual hierarchy for tick levels */
  .tick.major {
    opacity: 0.9;
  }
  .tick.minor {
    opacity: 0.75;
  }
  .tick.sub {
    opacity: 0.5;
  }

  .day-line {
    stroke: currentColor;
    stroke-width: 4;
  }
  text.day-label {
    font: 12px/1 system-ui;
    fill: var(--text);
  }
  rect.day-label {
    opacity: 0.7;
    fill: var(--bg);
  }
  .timeline-scrubber .buttons {
    position: absolute;
    left: 0;
    z-index: 100;
    border-right: var(--border);
    background: var(--surface);
    width: 20px;
    height: 36px;
    display: none;
    flex-direction: column;
    backdrop-filter: brightness(0%);
  }
  .timeline-scrubber .buttons button {
    cursor: pointer;
    box-sizing: border-box;
    border: 0;
    border-top: var(--border);
    border-left: 0;
    width: 20px;
    height: 18px;
    line-height: 1;
    color: var(--text);
    background: none;
  }

  .hover-preview {
    opacity: 0;
    transition: opacity 120ms linear;
    pointer-events: none;
  }
  .hover-preview[style*='display: none'] {
    opacity: 0;
  }
  .hover-preview:not([style*='display: none']) {
    opacity: 1;
  }

  .preview-line {
    stroke: var(--accent);
    stroke-width: 1;
  }
  .timeline-events .event-bar {
    stroke: var(--accent);
    stroke-width: 2;
    vector-effect: non-scaling-stroke;
  }
  .hover-event-icon {
    pointer-events: none;
    color: var(--accent);
  }
</style>
